import { Vec3, Quat, Track, RaceMode, RaceShipInfo } from '@voidline/shared';

/** Per-ship render/HUD state, interpolated to the display time. */
export interface ShipView {
  info: RaceShipInfo;
  pos: Vec3;
  quat: Quat;
  vel: Vec3;
  speed: number;
  grounded: boolean;
  boosting: boolean;
  drifting: boolean;
  respawning: boolean;
  finished: boolean;
  eliminated: boolean;
  inJump: boolean;
  wrongWay: boolean;
  boostEnergy: number;
  wallContact: number;
  steer: number;
  lap: number;
  nextCheckpoint: number;
  draftFactor: number;
  visible: boolean;
}

export function makeShipView(info: RaceShipInfo): ShipView {
  return {
    info,
    pos: new Vec3(),
    quat: new Quat(),
    vel: new Vec3(),
    speed: 0,
    grounded: true,
    boosting: false,
    drifting: false,
    respawning: false,
    finished: false,
    eliminated: false,
    inJump: false,
    wrongWay: false,
    boostEnergy: 0.5,
    wallContact: 0,
    steer: 0,
    lap: 1,
    nextCheckpoint: 1,
    draftFactor: 0,
    visible: true,
  };
}

export interface LocalRaceState {
  lap: number;
  totalLaps: number;
  nextCheckpoint: number;
  checkpointCount: number;
  raceTimeTicks: number;
  currentLapTicks: number;
  bestLapTicks: number;
  lastLapTicks: number;
  splitDeltaMs: number | null; // vs best lap at last checkpoint
  wrongWay: boolean;
  placement: number;
  finished: boolean;
  eliminated: boolean;
  finishTick: number;
  boostEnergy: number;
  speed: number;
  lapFraction: number;
}

export type RacePhase = 'countdown' | 'racing' | 'finished';

/** Everything the renderer + HUD need each frame. */
export interface RaceView {
  track: Track;
  mode: RaceMode;
  laps: number;
  phase: RacePhase;
  /** Seconds until green light (only meaningful in countdown). */
  countdownSeconds: number;
  /** Race clock in ticks (float — interpolated). */
  raceTicks: number;
  ships: ShipView[];
  standings: number[];
  /** Index into ships of the locally controlled ship, -1 while spectating. */
  localIndex: number;
  local: LocalRaceState | null;
  /** Ghost ship pose when a ghost is running. */
  ghost: { pos: Vec3; quat: Quat; visible: boolean } | null;
  /** Net diagnostics (null offline). */
  net: { ping: number; packetsPerSec: number; reconcileError: number; interpDelayMs: number } | null;
  /** Next elimination countdown seconds (elimination mode). */
  eliminationIn: number | null;
}

export interface GameEvents {
  onCountdownBeep?(n: number): void;
  onGo?(): void;
  onCheckpoint?(local: boolean): void;
  onLapComplete?(local: boolean, lapTicks: number, best: boolean): void;
  onFinish?(local: boolean, placement: number): void;
  onEliminated?(shipIndex: number, local: boolean): void;
  onWallHit?(intensity: number, local: boolean): void;
  onShipHit?(intensity: number, local: boolean): void;
  onBoostPad?(local: boolean): void;
  onLand?(intensity: number, local: boolean): void;
  onRespawn?(local: boolean): void;
  onFallOff?(local: boolean): void;
  onRaceEnd?(): void;
}

/** Common surface for LocalGame and NetGame. */
export interface GameDriver {
  readonly view: RaceView;
  /** Advance by real elapsed seconds; sample input internally. */
  update(dtSeconds: number): void;
  /** True once results should be shown. */
  isOver(): boolean;
  dispose(): void;
  setPaused?(paused: boolean): void;
}
